import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import {
  TextField,
  Button,
  FormControl,
  Typography,
  Checkbox,
  Box,
  FormControlLabel,
  Grid,
  Dialog,
  Autocomplete,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import heic2any from 'heic2any';
//import { RiHistoryFill } from 'react-icons/ri';
import { useTranslation } from 'react-i18next';

import SearchIcon from '@mui/icons-material/Search';
import { nowDateTimeInput } from '../../utils/dateUtils';

const apiUrl = process.env.REACT_APP_API_URL;
// تطبيع نص عربي/إنجليزي (إزالة التشكيل والتمطيط + تصغير)
const normalize = (s = '') =>
  s
    .toString()
    .trim()
    .replace(/\u0640/g, '') // حذف التطويل
    .replace(/[\u0617-\u061A\u064B-\u0652]/g, '') // حذف التشكيل
    .toLowerCase();

// عرض اسم الحصان "English | عربي" عند توفرهما
const horseDisplay = (h) => {
  const en = (h?.name || '').trim();
  const ar = (h?.name_arabic || '').trim();
  return ar && en ? `${en} | ${ar}` : en || ar || '';
};

const VetClinicRecordInside = () => {
  const { t, i18n } = useTranslation();
  const [openDialog, setOpenDialog] = useState(false);
  const [chipNumberInput, setChipNumberInput] = useState('');
  const navigate = useNavigate();

  //const user_id = localStorage.getItem('userId');
  const getCurrentDate = () => {
    return nowDateTimeInput();
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return '';
    const birthDate = new Date(dateOfBirth);
    if (Number.isNaN(birthDate.getTime())) return '';
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age -= 1;
    }
    return age >= 0 ? age : '';
  };

  const localizedPair = (arabicValue, englishValue) => {
    if (i18n.language === 'ar') {
      return arabicValue || englishValue || '';
    }
    return englishValue || arabicValue || '';
  };

  const formatHorseGender = (value) => {
    const normalizedValue = String(value || '').trim().toLowerCase();
    if (!normalizedValue) return '';
    if (['s', 'stallion', 'male'].includes(normalizedValue)) {
      return t('gender_stallion');
    }
    if (['m', 'mare', 'f', 'female'].includes(normalizedValue)) {
      return t('gender_mare');
    }
    if (['g', 'gelding'].includes(normalizedValue)) {
      return t('gender_gelding');
    }
    return value;
  };

  const [formData, setFormData] = useState({
    record_date: getCurrentDate(),
    horse_id: '',
    rfid_chip_number: '',
    horse_name: '',
    horse_color: '',
    horse_gender: '',
    horse_breed: '',
    horse_age: '',
    owner_id: '',
    owner_name: '',
    owner_phone: '',
    medical_history: '', // مهيّأة لتفادي تحذير uncontrolled→controlled
    diagnosis: '',
    treatment: '',
    procedures: [],
    medications: [],
    notes: '',
    total_amount: 0,
    file_uploads: [],
  });

  const [procedures, setProcedures] = useState([]);
  const [medications, setMedications] = useState([]);
  const [horses, setHorses] = useState([]);
  const [errors, setErrors] = useState({});
  //const uploadDate = new Date(); // Get current date

  const calculateTotalAmount = useCallback(() => {
    const proceduresTotal = formData.procedures.reduce((sum, procId) => {
      const proc = procedures.find((p) => p.procedure_id === parseInt(procId));
      return sum + (proc?.price || 0);
    }, 0);

    const medicationsTotal = formData.medications.reduce(
      (sum, med) => sum + (med.price || 0),
      0
    );

    setFormData((prevState) => ({
      ...prevState,
      total_amount: proceduresTotal + medicationsTotal,
    }));
  }, [formData.procedures, formData.medications, procedures]);

  useEffect(() => {
    calculateTotalAmount();
  }, [formData.procedures, formData.medications, calculateTotalAmount]);

  useEffect(() => {
    fetchDropdownData();
  }, []);

  const fetchDropdownData = async () => {
    try {
      const [proceduresResponse, medicationsResponse, horsesResponse] =
        await Promise.all([
        api.get(`${apiUrl}/procedures`),
        api.get(`${apiUrl}/medications`),
        api.get(`${apiUrl}/horses`),
      ]);

      if (
        proceduresResponse.data &&
        medicationsResponse.data &&
        horsesResponse.data
      ) {
        setProcedures(
          proceduresResponse.data.sort((a, b) =>
            a.procedure_name.localeCompare(b.procedure_name)
          )
        );
        setMedications(medicationsResponse.data);
        setHorses(
          [...horsesResponse.data].sort((a, b) =>
            (a.name || '').localeCompare(b.name || '', 'en', {
              sensitivity: 'base',
            })
          )
        );
      } else {
        console.error('One or more responses returned empty data');
      }
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
    }
  };

  const fetchHorseAndOwnerData = async (horse_id) => {
    try {
      const response = await api.get(
        `${apiUrl}/vetclinic-records-owner/${horse_id}`
      );
      const data = response.data;
      //console.log(data);

      setFormData((prevState) => ({
        ...prevState,
        horse_name: data.combined_name,
        rfid_chip_number: data.rfid_chip_number,
        horse_color: localizedPair(data.color_arabic, data.color_english),
        horse_gender: formatHorseGender(data.gender),
        horse_breed: localizedPair(data.breed_arabic, data.breed_english),
        horse_age: calculateAge(data.date_of_birth),
        owner_name: data.owner_name,
        owner_phone: data.owner_phone,
        owner_id: data.owner_id,
      }));
    } catch (error) {
      console.error('Error fetching horse and owner data:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: '',
    }));

    if (name === 'horse_id') {
      fetchHorseAndOwnerData(value);
    }
  };

  const handleProcedureChange = (procedure_id) => {
    setFormData((prevState) => {
      const isChecked = prevState.procedures.includes(procedure_id);
      const updatedProcedures = isChecked
        ? prevState.procedures.filter((id) => id !== procedure_id)
        : [...prevState.procedures, procedure_id];

      // إزالة رسالة الخطأ إذا تم تحديد إجراء واحد على الأقل
      setErrors((prevErrors) => ({
        ...prevErrors,
        procedures: updatedProcedures.length > 0 ? '' : prevErrors.procedures,
      }));

      return {
        ...prevState,
        procedures: updatedProcedures,
      };
    });
  };

  const handleMedicationChange = (index, field, value) => {
    setFormData((prevState) => {
      const updatedMedications = prevState.medications.map((med, i) => {
        if (i === index) {
          const updatedMed = { ...med, [field]: value };

          if (field === 'medication_id') {
            const selectedMed = medications.find(
              (m) => m.medication_id === parseInt(value)
            );
            if (selectedMed) {
              updatedMed.dosage = selectedMed.default_dosage || '';
              updatedMed.quantity = selectedMed.default_quantity || 1;
              updatedMed.unit = selectedMed.default_unit || '';
              updatedMed.duration = selectedMed.default_duration || '';
              updatedMed.price = selectedMed.price || 0;
            }
          }

          return updatedMed;
        }
        return med;
      });

      return {
        ...prevState,
        medications: updatedMedications,
      };
    });
  };

  const addMedication = () => {
    setFormData((prevState) => ({
      ...prevState,
      medications: [
        ...prevState.medications,
        { medication_id: '', dosage: '', quantity: '', unit: '', duration: '', price: 0 },
      ],
    }));
  };

  const removeMedication = (index) => {
    setFormData((prevState) => ({
      ...prevState,
      medications: prevState.medications.filter((_, i) => i !== index),
    }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files); // Convert files to array

    // Allow images including HEIC
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/heic'];

    // Convert HEIC files if present
    const validFiles = await Promise.all(
      files.map(async (file) => {
        if (file.type === 'image/heic') {
          try {
            const convertedBlob = await heic2any({
              blob: file,
              toType: 'image/jpeg',
            });
            return new File(
              [convertedBlob],
              file.name.replace(/.heic$/i, '.jpg'),
              {
                type: 'image/jpeg',
              }
            );
          } catch (error) {
            console.error('HEIC conversion error:', error);
            return null; // Handle error and skip this file
          }
        } else if (allowedTypes.includes(file.type)) {
          return file;
        }
        return null;
      })
    );

    // Filter out any invalid files
    const filteredValidFiles = validFiles.filter((file) => file !== null);

    if (filteredValidFiles.length === 0) {
      alert('Please upload valid images only (JPEG, PNG, GIF, HEIC).');
      return;
    }

    // Upload valid files
    const uploadPromises = filteredValidFiles.map((file) => {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file); // Ensure 'file' is used

      return api.post(`${apiUrl}/vetclinic-records/upload`, uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    });

    try {
      const responses = await Promise.all(uploadPromises);
      const uploadedFiles = responses.map((response) => response.data.filePath); // Extract file paths from response

      setFormData((prevState) => ({
        ...prevState,
        file_uploads: [...prevState.file_uploads, ...uploadedFiles], // Add uploaded files to the list
      }));
    } catch (error) {
      console.error('Error uploading files:', error);
      alert('Error uploading files. Please try again.');
    }
  };

  const validateFormData = () => {
    const newErrors = {};

    if (!formData.horse_id) newErrors.horse_id = 'Horse is required.';
    if (formData.procedures.length === 0)
      newErrors.procedures = 'At least one procedure must be selected.';

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0; // return true if no errors
  };

  const handleSubmit = async () => {
    if (!validateFormData()) {
      return;
    }

    //console.log('FormData to be submitted:', formData);

    try {
      const user_id = localStorage.getItem('userId');

      if (!user_id) {
        alert('User ID not found');
        return;
      }

      // Sending data via axios
      await api.post(`${apiUrl}/vetclinic-records`, {
        ...formData,
        user_id, // Add user_id here
        medical_history: formData.medical_history,
        diagnosis: formData.diagnosis,
        treatment: formData.treatment,
        work_place: 'Inside',
        procedures: formData.procedures.map((procId) => {
          const proc = procedures.find(
            (p) => p.procedure_id === parseInt(procId)
          );
          return { procedure_id: procId, price: proc ? proc.price : 0 };
        }),
        medications: formData.medications
          .filter((med) => med.medication_id)
          .map((med) => ({
            ...med,
            price: med.price || 0,
          })),
        file_uploads: formData.file_uploads, // Send the uploaded files
      });

      alert('Record added successfully!');
      navigate('/RecordList');

      setFormData({
        record_date: getCurrentDate(),
        horse_id: '',
        rfid_chip_number: '',
        horse_name: '',
        horse_color: '',
        horse_gender: '',
        horse_breed: '',
        horse_age: '',
        owner_id: '',
        owner_name: '',
        owner_phone: '',
        procedures: [],
        medications: [],
        total_amount: 0,
        file_uploads: [],
        notes: '',
      });
      setErrors({});
    } catch (error) {
      const message =
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Failed to add record. Please check your data and try again.';
      console.error('Error adding record:', message, error.response?.data || error);
      alert(message);
    }
  };

  const fetchHorseByChipNumber = async (chipNumber) => {
    try {
      const response = await api.get(`${apiUrl}/horses/rfid/${chipNumber}`);
      const data = response.data;

      // التحقق من البيانات التي تم إرجاعها
      //console.log('Horse Data:', data);

      if (data) {
        setFormData((prevState) => ({
          ...prevState,
          horse_name: data.horse_name || 'N/A', // تأكد من أن الحقل هو horse_name
          rfid_chip_number: data.rfid_chip_number || 'N/A',
          owner_name: data.owner_name || 'N/A',
          owner_phone: data.owner_phone || 'N/A',
          horse_color: '',
          horse_gender: '',
          horse_breed: '',
          horse_age: '',
          horse_id: data.horse_id,
          owner_id: data.owner_id,
        }));
        if (data.horse_id) {
          fetchHorseAndOwnerData(data.horse_id);
        }
        setOpenDialog(false); // اغلاق النافذة بعد استرجاع البيانات
      } else {
        alert('No data found for this chip number.');
      }
    } catch (error) {
      console.error('Error fetching horse data:', error);
      alert('Failed to fetch horse data.');
    }
  };

  const handleChipSearchClick = () => {
    setOpenDialog(true);
  };

  const handleFetchChipData = () => {
    if (chipNumberInput) {
      fetchHorseByChipNumber(chipNumberInput); // تأكد من وجود هذه الدالة أيضًا
    } else {
      alert('Please enter a valid RFID Chip Number');
    }
  };

  return (
    <div>
      <Box display="flex" justifyContent="center" mt={3} mb={2}>
        <Typography variant="h4">{t('vet_clinic_record_form')}</Typography>
      </Box>

      <TextField
        label="Date"
        type="datetime-local" // استخدام datetime-local لعرض التاريخ مع الوقت
        name="record_date"
        value={formData.record_date} // تأكد من أن formData.record_date يتم تحديثه بشكل صحيح
        fullWidth
        margin="dense"
        disabled
      />

      <FormControl fullWidth margin="dense" error={!!errors.horse_id}>
        <Grid item xs={11}>
          <Autocomplete
            options={horses}
            value={horses.find((h) => h.horse_id === formData.horse_id) || null}
            onChange={(_, newVal) => {
              // حفظ الـ horse_id ثم جلب بيانات المالك
              const horse_id = newVal?.horse_id || '';
              setFormData((prev) => ({ ...prev, horse_id }));
              if (horse_id) fetchHorseAndOwnerData(horse_id);
            }}
            getOptionLabel={(option) => horseDisplay(option)}
            isOptionEqualToValue={(option, value) =>
              String(option.horse_id) === String(value.horse_id)
            }
            renderOption={(props, option) => (
              // مفتاح فريد بالـ horse_id لتفادي تكرار المفاتيح عند تطابق الأسماء
              <li {...props} key={option.horse_id}>
                {horseDisplay(option)}
              </li>
            )}
            filterOptions={(options, state) => {
              const q = normalize(state.inputValue || '');
              return options.filter(
                (h) =>
                  normalize(h.name || '').includes(q) ||
                  normalize(h.name_arabic || '').includes(q)
              );
            }}
            renderInput={(params) => (
              <TextField {...params} label="Horse" margin="dense" fullWidth />
            )}
            ListboxProps={{ style: { maxHeight: 300 } }}
            clearOnBlur={false}
          />
        </Grid>

        {errors.horse_id && (
          <Typography color="error">{errors.horse_id}</Typography>
        )}
      </FormControl>

      <Box>
        <Grid container spacing={3} alignItems="center">
          {/* RFID Chip Number Field with Search Icon */}
          <Grid item xs={11}>
            {' '}
            {/* تخصيص مساحة أكبر للحقل */}
            <TextField
              label="RFID Chip Number"
              name="rfid_chip_number"
              value={formData.rfid_chip_number}
              fullWidth
              margin="dense"
              disabled
            />
          </Grid>

          <Grid item xs={1}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              ml={0.25}
            >
              <IconButton onClick={handleChipSearchClick}>
                <SearchIcon size={24} /> {/* تحديد حجم الأيقونة */}
              </IconButton>
            </Box>
          </Grid>

          {/* Dialog لإدخال رقم الشريحة */}
          <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
            <DialogTitle>Search Horse by RFID Chip Number</DialogTitle>
            <DialogContent>
              <TextField
                label="Enter RFID Chip Number"
                value={chipNumberInput}
                onChange={(e) => setChipNumberInput(e.target.value)}
                fullWidth
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleFetchChipData} color="primary">
                Fetch Data
              </Button>
              <Button onClick={() => setOpenDialog(false)} color="secondary">
                Cancel
              </Button>
            </DialogActions>
          </Dialog>
        </Grid>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label={t('color')}
            name="horse_color"
            value={formData.horse_color}
            fullWidth
            margin="dense"
            disabled
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label={t('gender')}
            name="horse_gender"
            value={formData.horse_gender}
            fullWidth
            margin="dense"
            disabled
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label={t('breed')}
            name="horse_breed"
            value={formData.horse_breed}
            fullWidth
            margin="dense"
            disabled
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label={t('age')}
            name="horse_age"
            value={formData.horse_age}
            fullWidth
            margin="dense"
            disabled
          />
        </Grid>
      </Grid>
      <TextField
        label="Owner Name"
        name="owner_name"
        value={formData.owner_name}
        fullWidth
        margin="dense"
        disabled
      />
      <TextField
        label="Owner Phone"
        name="owner_phone"
        value={formData.owner_phone}
        fullWidth
        margin="dense"
        disabled
      />

      <FormControl component="fieldset" margin="dense">
        <Typography>Procedures</Typography>
        <Grid container spacing={2}>
          {procedures.map((procedure) => (
            <Grid item xs={12} sm={6} md={4} key={procedure.procedure_id}>
              <FormControlLabel
                control={
                  <Checkbox
                    value={procedure.procedure_id}
                    checked={formData.procedures.includes(
                      procedure.procedure_id
                    )}
                    onChange={() =>
                      handleProcedureChange(procedure.procedure_id)
                    }
                  />
                }
                label={procedure.procedure_name}
              />
            </Grid>
          ))}
        </Grid>
        {errors.procedures && (
          <Typography color="error">{errors.procedures}</Typography>
        )}
      </FormControl>

      <FormControl
        component="fieldset"
        margin="dense"
        style={{ display: 'flex', flexWrap: 'wrap' }}
      >
        <Typography>Medications</Typography>
        {formData.medications.map((medication, index) => (
          <div key={index} style={{ marginBottom: '10px' }}>
            <Autocomplete
              options={medications}
              value={medications.find((med) => String(med.medication_id) === String(medication.medication_id)) || null}
              onChange={(_, selectedMedication) =>
                handleMedicationChange(index, 'medication_id', selectedMedication?.medication_id || '')
              }
              isOptionEqualToValue={(option, value) => String(option.medication_id) === String(value.medication_id)}
              getOptionLabel={(option) =>
                option?.medication_name
                  ? `${option.medication_name} - ${t('stock_quantity')}: ${option.stock_quantity ?? 0}`
                  : ''
              }
              getOptionDisabled={(option) => Number(option.stock_quantity || 0) <= 0}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('medication_name')}
                  margin="dense"
                  fullWidth
                />
              )}
              fullWidth
            />

            <TextField
              label="Dosage"
              name="dosage"
              value={medication.dosage}
              onChange={(e) =>
                handleMedicationChange(index, 'dosage', e.target.value)
              }
              fullWidth
              margin="dense"
            />
            <TextField
              label="Quantity"
              type="number"
              name="quantity"
              value={medication.quantity}
              onChange={(e) =>
                handleMedicationChange(index, 'quantity', e.target.value)
              }
              fullWidth
              margin="dense"
            />
            <TextField
              label="Unit"
              name="unit"
              value={medication.unit}
              onChange={(e) =>
                handleMedicationChange(index, 'unit', e.target.value)
              }
              fullWidth
              margin="dense"
            />
            <TextField
              label={t('medicationsList.defaultDuration')}
              name="duration"
              value={medication.duration || ''}
              onChange={(e) =>
                handleMedicationChange(index, 'duration', e.target.value)
              }
              fullWidth
              margin="dense"
            />
            <Button
              variant="contained"
              color="secondary"
              onClick={() => removeMedication(index)}
            >
              Remove Medication
            </Button>
          </div>
        ))}
        <Button variant="contained" color="primary" onClick={addMedication}>
          Add Medication
        </Button>
      </FormControl>
      <TextField
        label="Medical History"
        name="medical_history"
        value={formData.medical_history}
        onChange={handleChange}
        fullWidth
        margin="dense"
      />

      <TextField
        label="Diagnosis"
        name="diagnosis"
        value={formData.diagnosis}
        onChange={handleChange}
        fullWidth
        margin="dense"
      />

      <TextField
        label="Treatment"
        name="treatment"
        value={formData.treatment}
        onChange={handleChange}
        fullWidth
        margin="dense"
      />

      <TextField
        label="Notes"
        name="notes"
        value={formData.notes}
        onChange={handleChange}
        fullWidth
        margin="dense"
      />

      {false && (
        <>
          <TextField
            label="Total Amount"
            type="number"
            name="total_amount"
            value={formData.total_amount}
            onChange={handleChange}
            fullWidth
            margin="dense"
            disabled
          />
        </>
      )}

      <input type="file" multiple onChange={handleFileUpload} />
      <Box mt={2}>
        {formData.file_uploads.length > 0 && (
          <Typography variant="h6">Selected Files:</Typography>
        )}
        <ul>
          {formData.file_uploads.map((file, index) => (
            <li key={index}>{file}</li> // Display file paths
          ))}
        </ul>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: 2,
        }}
      >
        <Button variant="contained" color="primary" onClick={handleSubmit}>
          Submit
        </Button>
      </Box>
    </div>
  );
};

export default VetClinicRecordInside;
